import { FC, Ref, useState } from "react";
import { useSelector } from "react-redux";
import { Control, FieldValues, useWatch } from "react-hook-form";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  MultiOptionsDataType,
} from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

import LabelWithInfoIcon from "../../../../../../components/labelWithInfoIcon/LabelWithInfoIcon";
import { MAX_LENGTH_75 } from "../../../../../../helpers/constants";
import {
  setApiDataToDropDownOption,
  setApiDataToDropDownOptionForActivityType,
} from "../../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { MdmSearchActivityResultRowValue } from "../../../../../mdmActivitySearch/modelsAndSchemas/models";
import {
  ActivityDeliverableType,
  CreateActivityGeneralFormKey,
  CreateActivityGeneralProps,
} from "../../../../modelsAndSchemas/Models";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { ManageLocale } from "../../../../../../components/manageLocale/ManageLocale";
import { KeyValue } from "../../../../../../helpers/types/common";
import { userPreferredLanguageSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { ManageLocaleWarning } from "../../../manageLocaleWarning/ManageLocaleWarning";

interface CreateActivityProps extends FieldValues {
  control: Control<CreateActivityGeneralProps>;
  pageId: string;
  deliveryRef: Ref<{ resetValues: () => void }>;
  isFrameworkActivity: boolean;
  frameworkValue: string;
  activityDeliverables: ActivityDeliverableType[];
  participantStatusRef: Ref<{ resetValues: (values?: string[]) => void }>;
  modalityRef: Ref<{ resetValues: (values?: string[]) => void }>;
  activityTypeList: MdmSearchActivityResultRowValue[]; //change later
  modalityList?: MultiOptionsDataType[];
  isActivityDeliverablesSuccess: boolean;
  handleSaveManageLocal: (localeData: KeyValue) => void;
  manageLocaleTitle: string;
  showManageLocale: boolean;
  setShowManageLocale: (show: boolean) => void;
  bannerInfoMessage: string;
  isEditActivity?: boolean;
  topicNameValue: string;
  setTopicNameValue: (value: string) => void;
}

export const CreateActivityGeneral: FC<CreateActivityProps> = ({
  control,
  pageId,
  deliveryRef,
  activityDeliverables,
  isFrameworkActivity,
  frameworkValue,
  participantStatusRef,
  activityTypeList,
  modalityRef,
  modalityList,
  isActivityDeliverablesSuccess,
  handleSaveManageLocal,
  manageLocaleTitle,
  showManageLocale,
  setShowManageLocale,
  bannerInfoMessage,
  isEditActivity = false,
  topicNameValue,
  setTopicNameValue,
}) => {
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const [showLocaleWarning, setShowLocaleWarning] = useState(false);

  const topicNames = useWatch({
    name: CreateActivityGeneralFormKey.topic,
    control,
  });

  const handleTopicNameOnBlur = async (value: string) => {
    if (topicNameValue !== value && isEditActivity) {
      setTopicNameValue(value);
      setShowLocaleWarning(true);
    }
  };

  const handleCloseManageLocale = () => {
    setShowManageLocale(false);
    setShowLocaleWarning(false);
  };

  return (
    <MuiBox data-testid="activity-tab-general">
      <MuiGrid container className="activity-tab-general">
        {isFrameworkActivity && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiTextField
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.activityProgramFramework,
              )}
              id={`${pageId}-isFrameworkActivity`}
              value={frameworkValue}
              placeholder={commonLabels.inputPlaceHolder}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </MuiGrid>
        )}
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={CreateActivityGeneralFormKey.candidateStatus}
            ref={participantStatusRef}
          >
            <MuiMultiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.participantStatus,
              )}
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.candidateStatusList,
              )}
              showSearch
              showChip
              id={`${pageId}-${CreateActivityGeneralFormKey.candidateStatus}`}
              chipId={`${pageId}-${CreateActivityGeneralFormKey.candidateStatus}-chip`}
              dialogTitle={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.participantStatus,
              )}
              isMandatory
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={CreateActivityGeneralFormKey.activityType}
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.activityType,
              )}
              options={setApiDataToDropDownOptionForActivityType(
                activityTypeList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${CreateActivityGeneralFormKey.activityType}`}
              isMandatory
              placeholder={commonLabels.selectPlaceHolder}
              disabled={isFrameworkActivity}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={CreateActivityGeneralFormKey.modality}
            ref={modalityRef}
          >
            <MuiMultiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.modality,
              )}
              options={modalityList ?? []}
              showSearch
              showChip
              id={`${pageId}-${CreateActivityGeneralFormKey.modality}`}
              chipId={`${pageId}-${CreateActivityGeneralFormKey.modality}-chip`}
              dialogTitle={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.modality,
              )}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
              isMandatory
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiBox className="topic-name">
            <FormController
              control={control}
              name={`${CreateActivityGeneralFormKey.topic}.${userSelectedLanguage}`}
            >
              <MuiTextField
                label={fetchLabel(
                  createProgramSiteCoreKeys.createActivityKey,
                  createProgramSiteCoreKeys.topic,
                )}
                id={`${pageId}-topic`}
                isMandatory
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_75}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
                onBlur={(e) => handleTopicNameOnBlur(e.target.value)}
                shouldTrimSpaces
              />
            </FormController>
            <MuiLink
              className="topic-manage-locale-link"
              id={`${pageId}-topic-locale`}
              data-testid={`${pageId}-topic-locale`}
              onClick={() => setShowManageLocale(true)}
            >
              {commonLabels.manageLocale}
            </MuiLink>
          </MuiBox>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={CreateActivityGeneralFormKey.deliverables}
            ref={deliveryRef}
          >
            <MuiMultiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.deliverables,
              )}
              options={setApiDataToDropDownOption(activityDeliverables)}
              showSearch
              showChip
              id={`${pageId}-${CreateActivityGeneralFormKey.deliverables}`}
              chipId={`${pageId}-${CreateActivityGeneralFormKey.deliverables}-chip`}
              dialogTitle={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.selectedDeliverables,
              )}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
          {!activityDeliverables?.length && isActivityDeliverablesSuccess && (
            <LabelWithInfoIcon
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.noDeliverablesAvailable,
              )}
              tooltipMessage={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.noTaggedDeliverablesTooltip,
              )}
            />
          )}
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={CreateActivityGeneralFormKey.activityStatus}
          >
            <MuiSelect
              label={fetchLabel(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.activityStatus,
              )}
              options={fetchDropdownValues(
                createProgramSiteCoreKeys.createActivityKey,
                createProgramSiteCoreKeys.activityStatusList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${CreateActivityGeneralFormKey.activityStatus}`}
              isMandatory
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiBox>
        <MuiTypography
          id="id-group-label"
          variant="h2Bold"
          className="select-applicable"
          component="h2"
        >
          {fetchLabel(
            createProgramSiteCoreKeys.createActivityKey,
            createProgramSiteCoreKeys.selectApplicable,
          )}
        </MuiTypography>
      </MuiBox>
      <MuiBox role="group" aria-labelledby="id-group-label">
        <MuiGrid role="list" container className="checkbox-container">
          <MuiGrid size={gridSize} role="listitem">
            <FormControllerCheckbox
              control={control}
              name={CreateActivityGeneralFormKey.triggerBilling}
            >
              <MuiCheckbox
                checkboxLabel={fetchLabel(
                  createProgramSiteCoreKeys.createActivityKey,
                  createProgramSiteCoreKeys.triggerBilling,
                )}
                id={`${pageId}-${CreateActivityGeneralFormKey.triggerBilling}`}
                data-testid={`${pageId}-${CreateActivityGeneralFormKey.triggerBilling}`}
                tabIndex={0}
              />
            </FormControllerCheckbox>
          </MuiGrid>
          <MuiGrid size={gridSize} role="listitem">
            <FormControllerCheckbox
              control={control}
              name={CreateActivityGeneralFormKey.lastContactDate}
            >
              <MuiCheckbox
                checkboxLabel={fetchLabel(
                  createProgramSiteCoreKeys.createActivityKey,
                  createProgramSiteCoreKeys.countsTowardsLastContactDate,
                )}
                id={`${pageId}-${CreateActivityGeneralFormKey.lastContactDate}`}
                tabIndex={0}
              />
            </FormControllerCheckbox>
          </MuiGrid>
          <MuiGrid size={gridSize} role="listitem">
            <FormControllerCheckbox
              control={control}
              name={CreateActivityGeneralFormKey.triggerTouch}
            >
              <MuiCheckbox
                checkboxLabel={fetchLabel(
                  createProgramSiteCoreKeys.createActivityKey,
                  createProgramSiteCoreKeys.triggerTouchPointSurvey,
                )}
                id={`${pageId}-${CreateActivityGeneralFormKey.triggerTouch}`}
                tabIndex={0}
              />
            </FormControllerCheckbox>
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      {showManageLocale && (
        <ManageLocale
          show={showManageLocale}
          handleCloseModal={handleCloseManageLocale}
          localeLanguages={topicNames}
          onSaveManageLocalCallback={handleSaveManageLocal}
          title={manageLocaleTitle}
          bannerInfoMessage={bannerInfoMessage}
          maxLength={MAX_LENGTH_75}
        />
      )}
      {showLocaleWarning && (
        <ManageLocaleWarning
          show={showLocaleWarning}
          handleCloseModal={() => setShowLocaleWarning(false)}
          onPrimaryButtonClick={() => setShowManageLocale(true)}
          message={fetchLabel(
            createProgramSiteCoreKeys.createActivityKey,
            createProgramSiteCoreKeys.topicForOtherLanguagesWarningMessage,
          )}
        />
      )}
    </MuiBox>
  );
};
